import { Chip } from "@mui/material";
import { GridRowId } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import * as Sentry from "@sentry/react";
import { enqueueSnackbar } from "notistack";
import { MutableRefObject, createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import {
    TbBuilding,
    TbBuildingAirport,
    TbCancel,
    TbCar,
    TbClock,
    TbHeartHandshake,
    TbLocation,
    TbMapPin,
    TbNote,
    TbPlane,
    TbPlaneDeparture,
    TbStatusChange,
    TbUsers,
    TbVip,
} from "react-icons/tb";

import CommonAlert from "@/components/CommonAlert";
import { useNewSaveDataGridChanges } from "@/requests/transport.mutations";
import {
    PlanEditableFields,
    usePlanActions,
    usePlanChanges,
    usePlanShowConfirmationList,
} from "@/stores/use-plan-table";

function PlanUpdateDialog({ apiRef }: { apiRef: MutableRefObject<GridApiPremium> }) {
    const showConfirmationList = usePlanShowConfirmationList();
    const changes = usePlanChanges();
    const { t } = useTranslation();
    const { handleShowConfirmationList, handleRowModesModelCancel } = usePlanActions();

    const { mutateAsync, isPending } = useNewSaveDataGridChanges();

    const changeList = useMemo(
        () =>
            showConfirmationList.reduce(
                (prev, curr) => {
                    const keys = Object.keys(changes[curr] || {});

                    keys.forEach((key) => prev.list.add(key as keyof PlanEditableFields));
                    return prev;
                },
                {
                    list: new Set<keyof PlanEditableFields>(),
                }
            ),
        [showConfirmationList, changes]
    );

    const handleConfirm = async () => {
        if (isPending || !showConfirmationList.length) return;
        try {
            const payload = showConfirmationList.map((id: GridRowId) => {
                const rowChanges = JSON.parse(JSON.stringify(changes[id] || {}));
                const { airport, location: newLocation, driverId, transferType, ...rest } = rowChanges;
                let origin: string | number | undefined = undefined,
                    destination: string | number | undefined = undefined,
                    location: string | number | undefined = undefined;

                if (airport || newLocation) {
                    const isArrival = apiRef.current.getRow(id)?.isFlightArrival;
                    if (airport) {
                        if (isArrival) {
                            origin = airport;
                        } else {
                            destination = airport;
                        }
                    }
                    if (newLocation) {
                        if (isArrival) {
                            location = newLocation;
                        } else {
                            origin = newLocation;
                        }
                    }
                }
                if (transferType) {
                    rest.transferType = +transferType;
                }

                return {
                    ...rest,
                    driverId: driverId || (driverId === null ? null : undefined),
                    id: id as string,
                    origin,
                    destination,
                    location,
                };
            });

            const responses = await mutateAsync({
                changes: payload,
            });

            const completedList: GridRowId[] = [];

            for (const [index, response] of responses.entries()) {
                if (response.ok) completedList.push(response.id);
                setTimeout(() => {
                    if (!response.ok) {
                        Sentry.captureException(new Error(response.message));
                        enqueueSnackbar(response.message || "", {
                            variant: "error",
                        });
                        return;
                    }

                    handleRowModesModelCancel(response.id, apiRef.current, false);
                }, index * 80);
            }
            if (completedList.length)
                enqueueSnackbar(
                    t("responses.passengerUpdated", {
                        count: completedList.length > 1 ? 2 : 1,
                        length: completedList.length,
                    }),
                    {
                        variant: "success",
                    }
                );
        } catch (error) {
            console.error("Registration error:", error);
        }
        handleClose();
    };

    const handleClose = () => {
        handleShowConfirmationList([]);
    };

    return (
        <CommonAlert
            open={!!showConfirmationList.length}
            loading={isPending}
            description="confirmations.updatePassenger"
            color="primary"
            onClose={handleClose}
            handleConfirm={handleConfirm}
            html
            descriptionOpts={{
                count: showConfirmationList.length,
            }}
        >
            <div className="flex gap-2 flex-wrap py-1">
                {[...changeList.list].map((label) => (
                    <PlanUpdateDialogChip key={label} label={label} />
                ))}
            </div>
        </CommonAlert>
    );
}

export const editableOptions: Record<
    keyof PlanEditableFields,
    {
        icon: IconType;
    }
> = {
    airport: {
        icon: TbBuildingAirport,
    },
    driverId: {
        icon: TbCar,
    },
    pickUp: {
        icon: TbHeartHandshake,
    },
    location: {
        icon: TbLocation,
    },
    flightNumber: {
        icon: TbPlane,
    },
    transferType: {
        icon: TbVip,
    },
    origin: {
        icon: TbBuilding,
    },
    destination: {
        icon: TbBuilding,
    },
    companions: {
        icon: TbUsers,
    },
    status: {
        icon: TbStatusChange,
    },
    dropOff: {
        icon: TbClock,
    },
    note: {
        icon: TbNote,
    },
    isSelfTransport: {
        icon: TbMapPin,
    },
    flightTime: {
        icon: TbPlaneDeparture,
    },
    cancelled: {
        icon: TbCancel,
    },
};

export function PlanUpdateDialogChip({ label }: { label: keyof PlanEditableFields }) {
    const { t } = useTranslation();
    const item = editableOptions[label];

    if (!item) return null;

    return <Chip icon={createElement(item.icon)} label={t(label)} />;
}
export default PlanUpdateDialog;
